import { useEffect, useState } from "react";
import type { FC, ReactNode } from "react";
import { Link } from "@tanstack/react-router";
import { Plumb } from "@/components/plumb/Plumb";
import { APP, ARSENAL, BENCH_SRC, CASES, CHAPTERS, FOUNDER, LINKS, MAIL, MODES, depthOf } from "./content";
import { pad } from "./hooks";
import { Moving } from "./Passes";

/** What the workflows and tools are built with: one row of logos, each with what we use it for. */
export const Arsenal: FC = () => (
  <section id="arsenal" className="sd-arsenal">
    <header className="sd-cases-head">
      <p className="sd-label">our arsenal</p>
      <h2>we create workflows and tools using:</h2>
      <p className="sd-label">{pad(ARSENAL.length)} / in use</p>
    </header>
    <ol>
      {ARSENAL.map((tool, n) => (
        <li key={tool.name}>
          <span className="sd-label">{pad(n + 1)}</span>
          <h3 data-tall={tool.tall || undefined}>
            {tool.marks.map(src => (
              <img key={src} src={src} alt="" />
            ))}
            {tool.word ? <img src={tool.word} alt={tool.name} /> : <span>{tool.name}</span>}
          </h3>
          <p>{tool.use}</p>
        </li>
      ))}
    </ol>
  </section>
);

/**
 * The main projects as one shelf, each panel running a loop of its own film;
 * the open one shows its facts.
 */
export const Cases: FC = () => {
  const [open, setOpen] = useState(0);

  useEffect(() => {
    const pick = () => {
      const n = CASES.findIndex(work => `#${work.id}` === window.location.hash);
      if (n >= 0) setOpen(n);
    };
    pick();
    window.addEventListener("hashchange", pick);
    return () => window.removeEventListener("hashchange", pick);
  }, []);

  return (
    <section id="cases" className="sd-cases">
      <header className="sd-cases-head">
        <p className="sd-label">case studies</p>
        <h2>the projects the tools come from.</h2>
        <p className="sd-label">
          {pad(open + 1)} / {pad(CASES.length)}
        </p>
      </header>
      <div className="sd-shelf">
        {CASES.map((work, n) => (
          <article
            key={work.id}
            id={work.id}
            className="sd-case"
            data-open={open === n || undefined}
            tabIndex={0}
            onMouseEnter={() => setOpen(n)}
            onFocus={() => setOpen(n)}
            onClick={() => setOpen(n)}
          >
            <div className="sd-case-pic">
              <Moving src={work.loop} />
            </div>
            <p className="sd-case-no">
              <span>{pad(n + 1)}</span>
              <span>{work.code}</span>
            </p>
            <p className="sd-case-spine" aria-hidden>
              {work.title}
            </p>
            <div className="sd-case-card">
              <h3>{work.title}</h3>
              <p className="sd-case-meta">{work.meta}</p>
              <p className="sd-case-body">{work.body}</p>
              <dl>
                {work.facts.map(([key, value]) => (
                  <div key={key}>
                    <dt>{key}</dt>
                    <dd>{value}</dd>
                  </div>
                ))}
              </dl>
              <Link
                className="sd-case-more"
                to="/case/$id"
                params={{ id: work.id }}
                tabIndex={open === n ? undefined : -1}
              >
                <span>read the case study</span>
                <span aria-hidden>→</span>
              </Link>
            </div>
          </article>
        ))}
      </div>
    </section>
  );
};

/**
 * The app and the bench as one two-panel block: the picture on the left is read
 * live, and the readings are switched from the panel that introduces the app.
 */
export const Bench: FC = () => {
  const [n, setN] = useState(0);
  const pick = MODES[n];

  return (
    <section id="bench" className="sd-app">
      <div className="sd-app-pic">
        <img className="sd-cover" src={BENCH_SRC} alt="" />
        <Plumb
          className="sd-cover"
          src={BENCH_SRC}
          depth={depthOf(BENCH_SRC)}
          mode={pick.id}
          overlay={pick.overlay}
          nodes={8}
          tempo={0.3}
        />
        <p className="sd-bench-tag">{pick.label} / drawn live from the picture</p>
      </div>
      <div className="sd-app-text">
        <h2>{APP.text}</h2>
        <div>
          <p className="sd-label">{APP.bench}</p>
          <p className="sd-modes">
            {MODES.map((row, i) => (
              <button key={row.label} type="button" data-live={i === n || undefined} onClick={() => setN(i)}>
                {row.label}
              </button>
            ))}
          </p>
          <p className="sd-app-links">
            <a href={LINKS.app}>open genie ↗</a>
            <a href={LINKS.api}>ask for api access ↗</a>
          </p>
        </div>
      </div>
    </section>
  );
};

/** The closing block: one line, the way to the brief, and the address. */
export const Contact: FC = () => (
  <section id="contact" className="sd-contact">
    <p className="sd-label">start a project</p>
    <h2>tell us what you want to make. we will tell you how we would build it.</h2>
    <div className="sd-contact-row">
      <a href={`mailto:${MAIL}`}>{MAIL}</a>
      <Link className="sd-button" to="/contact">
        <span>start a project</span>
        <span aria-hidden>→</span>
      </Link>
    </div>
  </section>
);

/** A footer link to a part of the studio page: a jump on that page, a route from any other. */
const Jump: FC<{ home?: boolean; id: string; children: ReactNode }> = ({ home, id, children }) =>
  home ? (
    <a href={`#${id}`}>{children}</a>
  ) : (
    <Link to="/" hash={id}>
      {children}
    </Link>
  );

export const Footer: FC<{ home?: boolean }> = ({ home }) => (
  <footer className="sd-foot">
    <div className="sd-foot-cols">
      <div>
        <p className="sd-label">studio</p>
        {CHAPTERS.map(chapter => (
          <Jump key={chapter.id} home={home} id={chapter.id}>
            {chapter.title}
          </Jump>
        ))}
        <Jump home={home} id="arsenal">
          Arsenal
        </Jump>
        <Jump home={home} id="cases">
          Case studies
        </Jump>
        <Jump home={home} id="bench">
          The bench
        </Jump>
      </div>
      <div>
        <p className="sd-label">genie</p>
        <a href={LINKS.app}>Open the app</a>
        <a href={LINKS.api}>API access</a>
      </div>
      <div>
        <p className="sd-label">people</p>
        <Link to="/about">About us</Link>
        <Link to="/collaborators">Collaborators</Link>
        <Link to="/contact">Start a project</Link>
        <a href={`mailto:${MAIL}`}>{MAIL}</a>
      </div>
      <p className="sd-foot-note">
        <span>
          Genie - studio of applied AI*
          <br />[ artistic intelligence. ]
        </span>
        <span>Genie Art Inc.</span>
        <span>Founded by {FOUNDER}.</span>
      </p>
    </div>
    <p className="sd-wordmark" aria-hidden>
      <span>Genie</span>
      <span>Applied AI ™ 2016–2026</span>
    </p>
  </footer>
);
